import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaClock, 
  FaUser, 
  FaTag, 
  FaCheckCircle, 
  FaQuoteLeft, 
  FaArrowRight,
  FaSearch,
  FaTimes,
  FaEnvelope,
  FaInfoCircle
} from "react-icons/fa";
import "../Style/BlogDetail.css";

export const blogPosts = [
  {
    id: "maldives-eco-resort-compliance",
    title: "Navigating environmental mandates for structural resort infrastructure",
    excerpt: "An in-depth structural review of how luxury properties combine regional atoll preservation policies with international ISO 14001:2015 specifications.",
    category: "Sustainability",
    date: "July 12, 2026",
    author: "Compliance Engineering Unit",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=1400&q=80",
    featured: true,
    insight: "For island properties, environmental compliance works best when it is built into maintenance, procurement, utilities and marine operations—not held separately in an annual report.",
    actions: [
      "Maintain a live aspect-and-impact register for every operational area across land and lagoon zones.",
      "Track water, fuel, waste and reef-protection controls against strict monthly depletion targets.",
      "Test marine spill, wastewater treatment failure, and severe weather emergency response arrangements quarterly with ground teams."
    ],
    detailedContent: `
      Operating luxury hospitality infrastructure within fragile atoll ecosystems demands an uncompromising balance between guest expectation and strict environmental governance. Under regional Maldivian Environmental Protection Agency (EPA) frameworks and international ISO 14001:2015 specifications, resorts must look beyond surface-level green initiatives and build structural sustainability directly into their foundational operations.

      ### Comprehensive Environmental Aspects & Impacts
      Every physical intervention—from overwater villa foundation piling to desalination brine discharge—creates localized ecological ripples. Properties must establish a dynamic Aspect-Impact Register. This living document classifies operational activities by their direct consequence on marine biodiversity, freshwater lenses, and coastal erosion patterns, ensuring mitigation measures evolve as the resort ages.

      ### Integrating ISO 14001 into Daily Engineering Workflows
      Environmental management systems often fail when treated as standalone administrative paperwork. Successful luxury groups weave compliance metrics into the daily shift logs of chief engineers and waste facility managers. By automating utility monitoring and mandating immediate corrective reporting for any chemical or effluent anomaly, properties maintain elite environmental standing without sacrificing guest luxury or operational velocity.
    `
  },
  {
    id: "iso-9001-hospitality-benchmarks",
    title: "The alignment of QMS criteria inside multi-department atoll logistics",
    excerpt: "How scaling properties standardize service operations across separate coral reef properties while strictly retaining elite brand consistency maps.",
    category: "ISO Standards",
    date: "June 28, 2026",
    author: "Quality Assurance Specialist",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80",
    featured: false,
    insight: "A usable quality management system makes the guest experience repeatable while still giving local teams room to respond to the realities of a dispersed property.",
    actions: [
      "Define accountable process owners for every guest-facing and backend logistics touchpoint.",
      "Use one single cloud-controlled source for all standard operating procedures (SOPs) and revision histories.",
      "Turn guest feedback and operational nonconformities into tracked, time-bound continuous improvement actions."
    ],
    detailedContent: `
      Delivering seamless five-star service across geographically isolated island properties requires robust Quality Management Systems (QMS) built on ISO 9001 principles. When supply chains rely on seaplane transfers and multi-week cargo boats, standardizing service delivery without crushing local adaptability becomes the primary challenge for hospitality operators.

      ### Process Mapping Across Isolated Nodes
      Standardization begins by treating every department—from front-office concierge to marine sports and back-of-house laundry—as an interconnected process node. By defining clear input parameters, value-adding steps, and measurable output criteria, management can instantly pinpoint where service bottlenecks form and resolve them before they impact the guest journey.

      ### Continuous Feedback Loops and Corrective Action
      A true QMS is fundamentally a learning engine. When service discrepancies or equipment failures occur, automated logging tools route issues directly to department heads. Root-cause analysis protocols are then triggered, transforming isolated complaints into permanent structural upgrades across the entire property portfolio.
    `
  },
  {
    id: "cold-chain-assurance-logistics",
    title: "Securing deep ocean food transport via verified HACCP architectures",
    excerpt: "A tactical look at keeping international supply lines pristine from central shipping ports to local resort cold storage facilities.",
    category: "Food Safety",
    date: "June 14, 2026",
    author: "Food Safety Inspector",
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=1400&q=80",
    featured: false,
    insight: "Cold-chain assurance depends on continuous visibility: a temperature record only protects the operation when teams can act on an exception immediately.",
    actions: [
      "Set strict critical control limits and automated escalation steps for each transfer and storage point.",
      "Verify that all marine vessel and warehouse temperature-monitoring devices undergo monthly calibration.",
      "Audit supplier, cargo vessel, and resort receiving dock handovers to eliminate hidden traceability gaps."
    ],
    detailedContent: `
      Transporting perishable gastronomy across thousands of miles of open ocean to remote resort islands tests the limits of traditional supply chain logistics. Implementing Hazard Analysis Critical Control Point (HACCP) principles ensures that culinary operations maintain absolute food safety integrity from international origin ports straight to guest dining tables.

      ### Real-Time Telemetry and Temperature Thresholds
      Relying on manual clipboard checks during cargo unloading is no longer sufficient. Modern island kitchens utilize IoT-enabled data loggers embedded within refrigerated containers. These sensors transmit live temperature streams, triggering instant SMS or dashboard alerts if cold room integrity fluctuates even slightly during transit.

      ### Supplier Vetting and Port Handover Protocols
      The weakest link in any island food safety chain is invariably the transition point between cargo boats and resort jetties. Establishing rigorous acceptance protocols—including visual freshness inspections, core temperature probing, and immediate transfer to dedicated walk-in chillers—guarantees that only pristine ingredients enter production kitchens.
    `
  },
  {
    id: "resort-cybersecurity-annex-a",
    title: "Protecting private traveler data nodes from incoming endpoints",
    excerpt: "Implementing advanced cloud restriction layers based on ISO/IEC 27001 definitions to secure high-value transactions.",
    category: "Cybersecurity",
    date: "May 29, 2026",
    author: "InfoSec Lead Auditor",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1400&q=80",
    featured: false,
    insight: "Protecting traveler information starts with knowing where it travels, who can access it and how quickly access can be removed when circumstances change.",
    actions: [
      "Review privileged access rights and third-party vendor network connections on a monthly cadence.",
      "Document formal risk treatment decisions and applicable security controls in line with ISO/IEC 27001 Annex A.",
      "Conduct regular tabletop exercises simulating ransomware or data breach scenarios with executive teams."
    ],
    detailedContent: `
      Luxury resorts handle highly sensitive personal identifiable information (PII), financial records, and high-net-worth travel itineraries. Because modern properties rely heavily on guest-facing Wi-Fi networks, cloud property management systems (PMS), and third-party booking APIs, they represent lucrative targets for sophisticated cyber threat actors.

      ### Securing the Guest Wi-Fi and IoT Perimeter
      Smart rooms and guest-controlled villa automation systems introduce hundreds of connected endpoints. Implementing network segmentation ensures that guest entertainment and IoT gadgets reside on completely isolated virtual local area networks (VLANs) separate from administrative finance and payment card industry (PCI-DSS) processing environments.

      ### Rigorous Access Governance
      Following ISO/IEC 27001 frameworks, properties must enforce strict role-based access control (RBAC) combined with multi-factor authentication (MFA) for all staff members handling reservations and billing. When seasonal staff depart or vendor contracts conclude, automated de-provisioning protocols ensure digital credentials are instantly revoked.
    `
  },
  {
    id: "micro-grid-energy-optimization",
    title: "Optimizing standalone power generation structures with ISO 50001",
    excerpt: "Step-by-step guidance on systematically integrating hybrid solar arrays directly into standard central generator control configurations.",
    category: "Resort Operations",
    date: "May 15, 2026",
    author: "Energy Engineering Board",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1400&q=80",
    featured: false,
    insight: "Energy performance improves when a property combines reliable measurements, clear operating baselines and investment decisions tied to its actual demand profile.",
    actions: [
      "Identify significant energy uses (SEUs) across desalination, cooling compressors, and kitchen plants to establish baselines.",
      "Deploy intelligent sub-metering grids to investigate major load deviations and inefficiencies promptly.",
      "Measure all energy-saving retrofits against defined EnPIs (Energy Performance Indicators) to verify ROI."
    ],
    detailedContent: `
      Standalone island resorts operate as independent micro-grids, traditionally relying entirely on heavy diesel generator sets. Adopting ISO 50001 Energy Management Systems enables engineering teams to systematically reduce fuel consumption, integrate solar photovoltaic arrays, and stabilize power grids against extreme weather fluctuations.

      ### Balancing Hybrid Micro-Grids
      Synchronizing solar generation with diesel baseload generators requires sophisticated power management automation. By smoothing out transient cloud cover drops with advanced battery energy storage systems (BESS), resorts can safely run on high renewable penetration without risking resort-wide blackouts.

      ### Tracking Energy Performance Indicators (EnPIs)
      Effective energy management goes beyond tracking total monthly fuel bills. By normalizing energy consumption against occupancy rates, ambient seawater temperatures, and laundry volumes, engineering teams uncover hidden operational waste and optimize chiller plant sequencing dynamically.
    `
  },
  {
    id: "anti-bribery-procurement-safeguards",
    title: "Isolating transactional vulnerabilities inside supply network links",
    excerpt: "How deploying strategic ISO 37001 transparency registers filters out high-risk third-party operators completely.",
    category: "ISO Standards",
    date: "April 24, 2026",
    author: "Corporate Governance Director",
    readTime: "6 min read",
    image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS1vFS7Z5mRBMJ1XTE-4J6dmu9GUbFolhSHaQt1leLFRA&s=10",
    featured: false,
    insight: "A strong anti-bribery programme creates practical checks at the points where decisions, payments and supplier relationships carry the greatest risk.",
    actions: [
      "Apply due diligence assessments proportionate to supplier value, geographical risk, and transaction frequency.",
      "Maintain transparent digital ledgers documenting all procurement approvals, gifts, sponsorships, and hospitality exceptions.",
      "Provide secure, confidential whistleblower reporting channels and investigate all compliance concerns with total impartiality."
    ],
    detailedContent: `
      Global hospitality groups expanding across complex multi-jurisdictional supply chains face significant exposure to corruption and procurement fraud. Implementing ISO 37001 Anti-Bribery Management Systems establishes an uncompromising ethical framework that protects brand reputation and reassures international investors.

      ### Third-Party Risk Screening
      Procuring construction materials, marine fuel, and specialty imports often involves intermediary agents and overseas distributors. Rigorous onboarding due diligence ensures that every vendor clears background checks, signs strict anti-corruption covenants, and operates transparent billing practices.

      ### Culture of Transparency and Oversight
      Compliance is sustained through tone at the top and transparent internal audit trails. By segregating procurement approval duties—requiring multiple independent authorizations for large contracts—properties eliminate single-point financial vulnerabilities and foster an environment of unquestionable integrity.
    `
  },
  {
    id: "internal-audit-readiness-guide",
    title: "Preparing teams for an internal ISO audit without disrupting operations",
    excerpt: "A practical guide to scheduling audits, interviewing teams and turning findings into improvements that actually stick.",
    category: "ISO Standards", 
    date: "April 10, 2026", 
    author: "Lead Audit Team", 
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "An internal audit should give teams useful clarity before the external audit, rather than becoming a last-minute paperwork exercise.",
    actions: [
      "Build a balanced audit schedule around peak guest turnover and operational quiet periods.",
      "Brief department process owners on interview expectations, document sampling, and evidence gathering.",
      "Track all corrective action requests (CARs) through to verified closure and effectiveness sign-off."
    ],
    detailedContent: `
      Internal audits are frequently dreaded by resort department heads as disruptive bureaucratic hurdles. However, when approached strategically, internal audits act as invaluable health checks that empower staff, eliminate operational drift, and ensure external certification bodies encounter a polished, well-controlled management system.

      ### Constructive Audit Execution
      Auditors should approach interviews with curiosity rather than policing. By focusing on how frontline employees actually execute their daily tasks compared to documented SOPs, organizations can identify where documentation is overly complex and simplify procedures for better compliance.
    `
  },
  {
    id: "water-management-island-resorts",
    title: "Water stewardship controls for island resort operations",
    excerpt: "How desalination, consumption monitoring and wastewater controls can support resilient, environmentally responsible hospitality.",
    category: "Sustainability", 
    date: "March 26, 2026", 
    author: "Environmental Advisory Team", 
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Water management becomes more reliable when teams link plant performance, guest consumption and environmental controls in one operating view.",
    actions: [
      "Monitor daily RO (Reverse Osmosis) production, energy draw, and consumption metrics against established targets.",
      "Investigate sudden pressure drops or abnormal flow spikes immediately to mitigate hidden underground pipe leaks.",
      "Maintain rigorous daily testing protocols for treated wastewater before agricultural irrigation or deep-sea outfall."
    ],
    detailedContent: `
      Freshwater is the ultimate precious commodity on coral atolls. With no natural rivers or lakes, island resorts rely entirely on advanced Reverse Osmosis (RO) desalination plants and strict wastewater reclamation cycles to sustain luxurious guest experiences while safeguarding delicate local water tables.

      ### Advanced Desalination and Effluent Recycling
      Modern RO plants are energy intensive, requiring constant monitoring of membrane fouling rates and high-pressure pump efficiency. Simultaneously, treated greywater and blackwater are purified through membrane bioreactors (MBR) and reused for lush landscape irrigation, ensuring zero waste goes back into the pristine lagoon.
    `
  },
  {
    id: "supplier-evaluation-hospitality",
    title: "Building stronger supplier evaluation for hospitality supply chains",
    excerpt: "A supplier assurance framework that balances quality, food safety, ethics and dependable delivery for remote properties.",
    category: "Resort Operations", 
    date: "March 12, 2026", 
    author: "Supply Chain Advisory Unit", 
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Supplier evaluation is most effective when assessment criteria reflect the practical risks of the goods or services being purchased.",
    actions: [
      "Define strict pre-qualification approval criteria for all food, chemical, and engineering spare-part vendors.",
      "Review supplier fulfillment rates, lead times, and quality rejection logs on a quarterly performance scorecard.",
      "Keep digital due-diligence files, organic certificates, and insurance documents fully updated in central databases."
    ],
    detailedContent: `
      Supply chain disruptions in remote island locations can instantly compromise guest satisfaction. Developing a robust supplier evaluation methodology ensures that every vendor—from organic produce farmers to heavy machinery spare-part distributors—meets rigorous performance and ethical standards.
    `
  },
  {
    id: "incident-reporting-safety-culture",
    title: "Using incident reporting to build a stronger safety culture",
    excerpt: "Why simple, trusted reporting channels are essential for learning from near misses before they develop into major events.",
    category: "Resort Operations", 
    date: "February 27, 2026", 
    author: "Safety Systems Specialist", 
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "A reporting culture improves when people see that concerns lead to timely, fair and visible improvements.",
    actions: [
      "Make near-miss and hazard reporting quick, mobile-accessible, and entirely free from punitive reprisal.",
      "Analyse incident trends monthly to uncover systemic ergonomic, chemical, or marine hazard root causes.",
      "Share safety lessons learned across all departmental huddles and recognition programs."
    ],
    detailedContent: `
      A proactive safety culture depends entirely on psychological safety. When staff at all levels feel empowered to report slippery jetty tiles, frayed electrical cords, or chemical storage anomalies without fear, organizations prevent catastrophic accidents before they occur.
    `
  },
  {
    id: "guest-privacy-data-retention",
    title: "Guest privacy and data retention: setting practical controls",
    excerpt: "Key considerations for retaining booking, payment and guest preference information in a controlled and defensible way.",
    category: "Cybersecurity", 
    date: "February 11, 2026", 
    author: "Information Security Advisor", 
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Data retention rules should help teams keep what is needed while reducing the risk of holding sensitive information longer than necessary.",
    actions: [
      "Create a legally defensible, documented retention schedule for guest folios, passport copies, and preference profiles.",
      "Restrict database access privileges strictly to authorized reservations and front-office management personnel.",
      "Securely purge and anonymize personal records automatically once statutory retention periods expire."
    ],
    detailedContent: `
      Balancing personalized luxury service with stringent data privacy regulations requires disciplined records management. Resorts must retain enough historical guest preference data to deliver bespoke stays while ensuring compliance with international privacy mandates.
    `
  },
  {
    id: "waste-segregation-resort-operations",
    title: "Making waste segregation work across resort operations",
    excerpt: "Simple operational routines that improve recycling, reduce contamination and make waste data more useful for sustainability decisions.",
    category: "Sustainability", 
    date: "January 29, 2026", 
    author: "Sustainability Programme Team", 
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Segregation programmes succeed when bins, labels, collection routes and team training all support the same simple process.",
    actions: [
      "Standardise waste collection stations, color-coded bins, and intuitive multi-lingual visual labels across back-of-house areas.",
      "Measure daily contamination rates and recycling diversion volumes to quantify environmental impact.",
      "Train all incoming staff and external contractors on proper organic, glass, plastic, and hazardous waste handling."
    ],
    detailedContent: `
      Managing waste on isolated islands presents unique logistical hurdles. Without municipal landfills nearby, resorts must operate state-of-the-art compression, composting, and glass-crushing facilities on-site, making rigorous front-line segregation essential for successful recycling.
    `
  },
  {
    id: "food-allergen-management",
    title: "Food allergen management for high-volume hospitality kitchens",
    excerpt: "Controls kitchens can use to communicate allergens, prevent cross-contact and respond confidently to guest dietary needs.",
    category: "Food Safety", 
    date: "January 15, 2026", 
    author: "Food Safety Inspector", 
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Allergen controls are strongest when menus, purchasing, preparation and service teams are all working from accurate information.",
    actions: [
      "Maintain verified, up-to-date allergen specifications for every ingredient and menu creation.",
      "Designate separate preparation zones, utensils, and fryers where severe cross-contact risks exist.",
      "Train service staff to communicate guest dietary restrictions clearly to kitchen expeditors."
    ],
    detailedContent: `
      With guests traveling from across the globe, culinary teams must maintain immaculate allergen controls. Clear communication channels between front-of-house hosts and executive chefs prevent catastrophic allergic reactions while ensuring exceptional dining experiences.
    `
  },
  {
    id: "business-continuity-weather-events",
    title: "Business continuity planning for severe weather and supply disruption",
    excerpt: "How organisations can identify critical activities and prepare practical response plans for disruptions that affect island operations.",
    category: "Resort Operations", 
    date: "December 18, 2025", 
    author: "Continuity Planning Group", 
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Continuity plans become dependable only when recovery priorities, responsibilities and communications have been tested in realistic scenarios.",
    actions: [
      "Identify critical life-safety services, power backup thresholds, and emergency communication protocols.",
      "Maintain emergency buffer stocks of fuel, potable water, medical supplies, and non-perishable food items.",
      "Test disaster recovery and evacuation scenarios through scheduled team drills annually."
    ],
    detailedContent: `
      Tropical weather patterns and occasional severe monsoons demand robust business continuity plans. Ensuring island properties can sustain critical life-safety functions and guest communications during sudden isolation is paramount for luxury resort management.
    `
  },
  {
    id: "management-review-effective-decisions",
    title: "How management reviews can drive effective ISO decisions",
    excerpt: "A focused approach to management review meetings that uses evidence, risks and performance data to guide real improvement.",
    category: "ISO Standards", 
    date: "December 04, 2025", 
    author: "Quality Assurance Specialist", 
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1400&q=80", 
    featured: false,
    insight: "Management review works best when leaders make decisions on priorities, resources and improvement actions—not simply acknowledge reports.",
    actions: [
      "Prepare concise performance summaries, audit results, and risk registers well in advance of meetings.",
      "Assign explicit executive owners and firm completion deadlines for every strategic decision made.",
      "Review prior meeting action items for verified completion and measurable operational effectiveness."
    ],
    detailedContent: `
      The annual management review is the cornerstone of any ISO-certified management system. When executive leadership treats this meeting as a strategic steering session rather than a rubber-stamping exercise, the entire organization aligns rapidly toward continuous improvement.
    `
  }
];

function Blog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeModalPost, setActiveModalPost] = useState(null);
  const [emailInput, setEmailInput] = useState("");

  const categories = ["All", "Sustainability", "ISO Standards", "Food Safety", "Cybersecurity", "Resort Operations"];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    alert(`Subscribed successfully: ${emailInput}`);
    setEmailInput("");
  };

  if (id) {
    const post = blogPosts.find((p) => p.id === id);

    if (!post) {
      return (
        <div className="blog-detail-page">
          <div className="blog-detail-container blog-detail-not-found">
            <h2>Article Not Found</h2>
            <p>The strategic insights article you are looking for does not exist or has been relocated.</p>
            <Link to="/blog" className="blog-back-link" style={{ color: "#1c2f6e", marginTop: "1.5rem" }}>
              <FaArrowLeft /> Return to Knowledge Base
            </Link>
          </div>
        </div>
      );
    }

    const relatedPosts = blogPosts
      .filter((p) => p.id !== post.id)
      .slice(0, 3);

    return (
      <div className="blog-detail-page">
        <header className="blog-detail-hero" style={{ backgroundImage: `url(${post.image})` }}>
          <div className="blog-detail-hero-shade"></div>
          <div className="blog-detail-container blog-detail-hero-content">
            <Link to="/blog" className="blog-back-link">
              <FaArrowLeft /> Back to Knowledge Base
            </Link>
            <div>
              <span className="blog-detail-category">{post.category}</span>
            </div>
            <h1>{post.title}</h1>
            <p>{post.excerpt}</p>
            <div className="blog-detail-meta">
              <span><FaCalendarAlt /> {post.date}</span>
              <span><FaUser /> {post.author}</span>
              <span><FaClock /> {post.readTime}</span>
            </div>
          </div>
        </header>

        <div className="blog-detail-container">
          <div className="blog-detail-layout">
            <article className="blog-detail-article">
              <p className="blog-detail-lead">{post.excerpt}</p>

              <section>
                <h2>Overview &amp; Context</h2>
                <div style={{ whiteSpace: "pre-line", lineHeight: "1.8", color: "#4a5568" }}>
                  {post.detailedContent || "Deploying structured standard frameworks across regional or distributed infrastructure requires clear alignment between management targets and operational realities. This document outlines core considerations for sustaining long-term compliance without disrupting day-to-day productivity."}
                </div>
              </section>

              <section style={{ marginTop: "2.5rem" }}>
                <h2>Strategic Insight</h2>
                <div className="blog-detail-callout">
                  <p style={{ margin: 0, fontWeight: 600, color: "#1c2f6e", lineHeight: "1.6" }}>
                    <FaQuoteLeft style={{ marginRight: "0.5rem", color: "#f5b400" }} />
                    {post.insight}
                  </p>
                </div>
              </section>

              <section style={{ marginTop: "2.5rem" }}>
                <h2>Recommended Operational Actions</h2>
                <p>To implement and verify these measures effectively, consider integrating the following steps into your internal audit cycles:</p>
                <div className="blog-detail-callout" style={{ marginTop: "1rem" }}>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {post.actions.map((act, index) => (
                      <li key={index} style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem", alignItems: "flex-start" }}>
                        <FaCheckCircle style={{ color: "#1c2f6e", marginTop: "4px", flexShrink: 0 }} />
                        <span>{act}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </article>

            <aside className="blog-detail-sidebar">
              <div className="blog-detail-author-card">
                <span>Author Profile</span>
                <h3>{post.author}</h3>
                <p>Specialized advisory unit focused on robust compliance frameworks and operational optimization.</p>
              </div>

              <div className="blog-detail-sidebar-card">
                <span>Advisory Support</span>
                <h3>Need Implementation Guidance?</h3>
                <p>Speak directly with our senior compliance engineers to map out your organization's roadmap.</p>
                <Link to="/contact" style={{ marginTop: "1rem" }}>
                  Request Consultation <FaArrowRight />
                </Link>
              </div>
            </aside>
          </div>
        </div>

        <section className="blog-detail-related">
          <div className="blog-detail-container">
            <h2>Related Insights</h2>
            <div className="blog-detail-related-grid">
              {relatedPosts.map((related) => (
                <Link 
                  key={related.id} 
                  to={`/blog/${related.id}`} 
                  className="blog-detail-related-card"
                >
                  <img src={related.image} alt={related.title} />
                  <span>{related.category}</span>
                  <h3>{related.title}</h3>
                  <small>Read Article <FaArrowRight /></small>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts.find((p) => p.featured) || blogPosts[0];

  return (
    <div className="blog-matrix-layout">
      <header className="blog-hero-header">
        <div className="matrix-container blog-hero-inner">
          <span className="matrix-badge">Knowledge Hub</span>
          <h1>Strategic Insights &amp; Advisory</h1>
          <p>
            Expert perspectives on ISO compliance, sustainable resort infrastructure, 
            food safety protocols, and operational resilience in the Maldives.
          </p>

          <div className="blog-search-bar">
            <input 
              type="text" 
              placeholder="Search strategic insights or keywords..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FaSearch className="search-ico" />
          </div>
        </div>
      </header>

      <nav className="blog-filter-bar">
        <div className="matrix-container filter-row">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`blog-filter-btn ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </nav>

      <main className="matrix-container blog-main-grid-area">
        {selectedCategory === "All" && searchQuery === "" && featuredPost && (
          <div 
            className="featured-blog-banner"
            onClick={() => setActiveModalPost(featuredPost)}
          >
            <div className="featured-banner-img" style={{ backgroundImage: `url(${featuredPost.image})` }}></div>
            <div className="featured-banner-content">
              <span className="featured-tag"><FaTag /> Featured Article</span>
              <span className="blog-cat">{featuredPost.category}</span>
              <h2>{featuredPost.title}</h2>
              <p>{featuredPost.excerpt}</p>
              <div className="featured-meta">
                <span><FaCalendarAlt /> {featuredPost.date}</span>
                <span><FaClock /> {featuredPost.readTime}</span>
              </div>
              <span className="read-more-link">Read Full Article <FaArrowRight /></span>
            </div>
          </div>
        )}

        <div className="blog-cards-grid">
          {filteredPosts.map((post) => (
            <article 
              key={post.id} 
              className="blog-card-item"
              onClick={() => setActiveModalPost(post)}
            >
              <div className="blog-card-thumb" style={{ backgroundImage: `url(${post.image})` }}>
                <span className="card-cat-badge">{post.category}</span>
              </div>
              <div className="blog-card-info">
                <div className="blog-card-date-read">
                  <span><FaCalendarAlt /> {post.date}</span>
                  <span><FaClock /> {post.readTime}</span>
                </div>
                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>
                <div className="blog-card-footer">
                  <span className="author-text"><FaUser /> {post.author}</span>
                  <span className="arrow-circle"><FaArrowRight /></span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="blog-empty-state">
            <FaInfoCircle className="empty-state-icon" />
            <h3>No Articles Found</h3>
            <p>Try searching with different keywords or choosing another category tab.</p>
          </div>
        )}
      </main>

      {/* TOP-SLIDING FULL BLOG DETAIL MODAL POPUP */}
      {activeModalPost && (
        <div className="blog-modal-backdrop" onClick={() => setActiveModalPost(null)}>
          <div className="blog-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="blog-modal-close-btn" onClick={() => setActiveModalPost(null)}>
              <FaTimes />
            </button>
            
            <div className="blog-modal-image-wrapper" style={{ backgroundImage: `url(${activeModalPost.image})` }}>
              <div className="blog-detail-hero-shade"></div>
              <span className="blog-modal-category-tag">{activeModalPost.category}</span>
            </div>

            <div className="blog-modal-content">
              <div className="post-meta-row">
                <span className="meta-item"><FaCalendarAlt /> {activeModalPost.date}</span>
                <span className="meta-item"><FaUser /> {activeModalPost.author}</span>
                <span className="meta-item"><FaClock /> {activeModalPost.readTime}</span>
              </div>

              <h2>{activeModalPost.title}</h2>
              
              <div className="blog-modal-body-text">
                <p className="blog-detail-lead">{activeModalPost.excerpt}</p>

                <section style={{ marginTop: "2rem" }}>
                  <h3>Overview &amp; Context</h3>
                  <div style={{ whiteSpace: "pre-line", lineHeight: "1.8", color: "#4a5568" }}>
                    {activeModalPost.detailedContent || "Deploying structured standard frameworks across regional or distributed infrastructure requires clear alignment between management targets and operational realities."}
                  </div>
                </section>

                <section style={{ marginTop: "2rem" }}>
                  <h3>Strategic Insight</h3>
                  <div className="blog-detail-callout">
                    <p style={{ margin: 0, fontWeight: 600, color: "#1c2f6e", lineHeight: "1.6" }}>
                      <FaQuoteLeft style={{ marginRight: "0.5rem", color: "#f5b400" }} />
                      {activeModalPost.insight}
                    </p>
                  </div>
                </section>

                <section style={{ marginTop: "2rem" }}>
                  <h3>Recommended Operational Actions</h3>
                  <p>To implement and verify these measures effectively, consider integrating the following steps into your internal audit cycles:</p>
                  <div className="blog-detail-callout" style={{ marginTop: "1rem" }}>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {activeModalPost.actions.map((act, index) => (
                        <li key={index} style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem", alignItems: "flex-start" }}>
                          <FaCheckCircle style={{ color: "#1c2f6e", marginTop: "4px", flexShrink: 0 }} />
                          <span>{act}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="matrix-newsletter-dock" style={{ marginTop: "4rem" }}>
        <div className="matrix-container newsletter-flex-wrapper">
          <div className="newsletter-text-block">
            <div className="dock-title-row">
              <FaEnvelope className="envelope-icon" />
              <h4>Subscribe to Intelligence Briefings</h4>
            </div>
            <p>Get immediate operational updates, audit circulars, and framework risk analysis worksheets directly inside your inbox.</p>
          </div>
          <form className="newsletter-input-row" onSubmit={handleSubscribe}>
            <input 
              type="email" 
              placeholder="Enter corporate email address..." 
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              required
            />
            <button type="submit" className="newsletter-cta-btn button-image-bg-effect">
              Join Briefing Registry
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default Blog;